import React, { useState, useRef } from "react";
import { Download, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import backendApi from "@/api/backendApi";

// Backend file URLs are served from the same origin as the API. Falls back to
// localhost for first-time local setup; production must set REACT_APP_API_URL.
const FILE_HOST = (process.env.REACT_APP_API_URL || "http://localhost:5000").replace(/\/+$/, "");

export default function ResumeSection({ profile }) {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // MUTATIONS
  const uploadMutation = useMutation({
    mutationFn: (formData) => backendApi.entities.UserProfile.uploadResume(formData),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => backendApi.entities.UserProfile.deleteResume(),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
    },
  });

  // ----------------------------
  // Resume Upload Handler
  // ----------------------------
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Client side check
    if (file.size > 2 * 1024 * 1024) {
      alert("File size exceeds 2MB limit.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("resume", file);
      await uploadMutation.mutateAsync(formData);
    } catch (err) {
      console.error("RESUME UPLOAD FAILED:", err);
      alert(err.message || "Failed to upload resume.");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete your resume?")) {
      deleteMutation.mutate();
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Resume</h3>

      {/* ---------------------------------------------------- */}
      {/* If Resume Already Uploaded */}
      {/* ---------------------------------------------------- */}
      {profile.resume_url ? (
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">

            {/* Resume Info */}
            <div>
              <p className="font-medium text-gray-800">
                {profile.resume_name || "resume.pdf"}
              </p>

              <p className="text-sm text-gray-500">
                Uploaded on{" "}
                {profile.resume_uploaded_date
                  ? new Date(profile.resume_uploaded_date).toLocaleDateString(
                    "en-IN",
                    { month: "short", day: "2-digit", year: "numeric" }
                  )
                  : "Unknown date"}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {/* DOWNLOAD */}
              <a
                href={`${FILE_HOST}${profile.resume_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <Download className="w-5 h-5 text-gray-500" />
              </a>

              {/* DELETE */}
              <button
                onClick={handleDelete}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                title="Delete Resume"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ---------------------------------------------------- */
        /* No Resume Uploaded, Show Upload UI */
        /* ---------------------------------------------------- */
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-2">Upload your resume</p>
          <p className="text-sm text-gray-400 mb-4">
            Supported Formats: doc, docx, rtf, pdf, up to 2 MB
          </p>
        </div>
      )}

      {/* ---------------------------------------------------- */}
      {/* Action Button (Upload / Update) */}
      {/* ---------------------------------------------------- */}
      <div className="flex flex-col items-center mt-6">
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept=".doc,.docx,.rtf,.pdf"
          onChange={handleFileUpload}
          disabled={uploading}
        />

        <Button
          variant={profile.resume_url ? "outline" : "default"}
          className={`rounded-full px-10 h-10 font-medium ${!profile.resume_url ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
          disabled={uploading}
          onClick={handleButtonClick}
        >
          {uploading ? (
            "Uploading..."
          ) : profile.resume_url ? (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Update Resume
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload Resume
            </>
          )}
        </Button>

        <p className="text-[12px] text-gray-400 mt-2">
          Supported formats: doc, docx, rtf, pdf, max 2 MB
        </p>
      </div>

      {/* ---------------------------------------------------- */}
      {/* AI Resume Analysis */}
      {/* ---------------------------------------------------- */}
      {profile.resume_analysis && Object.keys(profile.resume_analysis).length > 0 && (
        <div className="mt-8 border-t pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-bold">AI</span>
            </div>
            <h4 className="font-semibold text-gray-800">Resume Insights</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
              <p className="text-sm text-gray-500 mb-1">ATS Score</p>
              <p className="text-2xl font-bold text-gray-800">
                {profile.resume_analysis.score}/100
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
              <p className="text-sm text-gray-500 mb-1">Detected Role</p>
              <p className="font-semibold text-gray-800 line-clamp-1">
                {profile.resume_analysis.detected_role || "Unknown"}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
              <p className="text-sm text-gray-500 mb-1">Experience Level</p>
              <p className="font-semibold text-gray-800 line-clamp-1">
                {profile.resume_analysis.experience_level || "Unknown"}
              </p>
            </div>
          </div>

          {profile.resume_analysis.suggestions?.length > 0 && (
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
              <h5 className="text-sm font-semibold text-orange-800 mb-2">Suggestions to improve</h5>
              <ul className="list-disc list-inside text-sm text-orange-700 space-y-1">
                {profile.resume_analysis.suggestions.map((sug, i) => (
                  <li key={i}>{sug}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
